import type { GeoReport } from "../api/geoService";

export interface RankedEntry {
  name: string;
  count: number;
  /** true = 这一行是本品牌自己(不是竞品)。 */
  own: boolean;
}

/**
 * 把「本品牌」按 competitor_ranking 同一套计数口径并进同一份排行(0907 重排 · 竞品可见度
 * 排行条 highlight 本品牌 + Hero「竞品可见度位次」KPI 共用)。
 *
 * `competitor_ranking` 只含**竞品**的计数(后端 comp_rank 循环 probes[].competitors,不含
 * 品牌自身)。要把品牌摆进同一张榜,必须用**同一批探针、同一种计数口径**算出品牌自己的计数
 * —— 不能拿 search_overview.mention_hits(那是「全网搜索概览」另一套样本,分母不同)。
 *
 * 后端 comp_rank 的输入是 `memory_probes || search_probes`(GEO_MEMORY_ENGINES 默认关,
 * 绝大多数轮次即 search_probes = probes.filter(mode==='search'))。report.probes 是三段拼起来
 * 的全量(含 derived),这里同样按 mode==='search' 过滤对齐口径;memory 档打开的极少数轮次
 * 会有一点误差,但仍是「用真实字段算出来的位次」,不是编的数字。
 *
 * ranking 或(同口径)探针任一缺席 → 返回 null,调用方应整卡/整项不渲染,不编数字。
 */
export function rankWithBrand(report: GeoReport): RankedEntry[] | null {
  const ranking = report.competitor_ranking ?? [];
  const searchProbes = (report.probes ?? []).filter((p) => p.mode === "search");
  if (ranking.length === 0 || searchProbes.length === 0) return null;
  // 1006:优先用后端与提及率同池的 brand_rank.brand_mentions —— 本地按 probes[].mentioned 数会把
  //   「题面本身带品牌名」的题也算进去(演示机:提及率 0 次,排行却写本品牌 4 次)。缺了才回退本地计数。
  const pooled = report.search_overview?.brand_rank?.brand_mentions;
  const brandCount =
    typeof pooled === "number" ? pooled : searchProbes.filter((p) => p.mentioned).length;
  const others = ranking.filter(([name]) => name !== report.brand);
  const combined: RankedEntry[] = [
    ...others.map(([name, count]) => ({ name, count, own: false })),
    { name: report.brand, count: brandCount, own: true },
  ];
  combined.sort((a, b) => b.count - a.count);
  return combined;
}
